import React, { useState, useRef } from 'react';
import { Save, Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { useSettings } from '../../hooks/useSettings';
import toast from 'react-hot-toast';

export default function BrandingSettings() {
  const { settings, loading, updateSettings, uploadFile } = useSettings();
  const [uploading, setUploading] = useState<{ logo?: boolean; header?: boolean; footer?: boolean }>({});
  const logoInputRef = useRef<HTMLInputElement>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const footerInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (file: File, type: 'logo' | 'header' | 'footer') => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading({ ...uploading, [type]: true });

    const result = await uploadFile(file, type);

    if (result.success && result.url) {
      const fieldMap = {
        logo: 'logo_url',
        header: 'report_header_url',
        footer: 'report_footer_url',
      };

      const updateResult = await updateSettings({
        [fieldMap[type]]: result.url,
      });

      if (updateResult.success) {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully`);
      } else {
        toast.error(updateResult.error || 'Failed to save image');
      }
    } else {
      toast.error(result.error || 'Failed to upload image');
    }

    setUploading({ ...uploading, [type]: false });
  };

  const handleRemoveImage = async (type: 'logo' | 'header' | 'footer') => {
    const fieldMap = {
      logo: 'logo_url',
      header: 'report_header_url',
      footer: 'report_footer_url',
    };

    const result = await updateSettings({
      [fieldMap[type]]: null,
    });

    if (result.success) {
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} removed successfully`);
    } else {
      toast.error(result.error || 'Failed to remove image');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Logo Upload */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Hospital Logo</h3>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          {settings.logo_url ? (
            <div className="flex items-start space-x-4">
              <img
                src={settings.logo_url}
                alt="Hospital Logo"
                className="w-32 h-32 object-contain border border-gray-200 rounded"
              />
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">Current logo</p>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading.logo}
                    className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                  >
                    <Upload className="w-4 h-4" />
                    <span>{uploading.logo ? 'Uploading...' : 'Replace'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveImage('logo')}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span>Remove</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploading.logo}
                  className="flex items-center space-x-2 mx-auto px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  <span>{uploading.logo ? 'Uploading...' : 'Upload Logo'}</span>
                </button>
                <p className="mt-2 text-xs text-gray-500">PNG, JPG up to 5MB</p>
              </div>
            </div>
          )}
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload(file, 'logo');
            }}
          />
        </div>
      </div>

      {/* Report Header Image */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Report/Invoice Header Image</h3>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          {settings.report_header_url ? (
            <div className="flex items-start space-x-4">
              <img
                src={settings.report_header_url}
                alt="Report Header"
                className="w-full max-w-md h-32 object-contain border border-gray-200 rounded"
              />
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">Current header image</p>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => headerInputRef.current?.click()}
                    disabled={uploading.header}
                    className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                  >
                    <Upload className="w-4 h-4" />
                    <span>{uploading.header ? 'Uploading...' : 'Replace'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveImage('header')}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span>Remove</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => headerInputRef.current?.click()}
                  disabled={uploading.header}
                  className="flex items-center space-x-2 mx-auto px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  <span>{uploading.header ? 'Uploading...' : 'Upload Header Image'}</span>
                </button>
                <p className="mt-2 text-xs text-gray-500">PNG, JPG up to 5MB (Recommended: 1200x200px)</p>
              </div>
            </div>
          )}
          <input
            ref={headerInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload(file, 'header');
            }}
          />
        </div>
      </div>

      {/* Report Footer Image */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Report/Invoice Footer Image</h3>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          {settings.report_footer_url ? (
            <div className="flex items-start space-x-4">
              <img
                src={settings.report_footer_url}
                alt="Report Footer"
                className="w-full max-w-md h-32 object-contain border border-gray-200 rounded"
              />
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">Current footer image</p>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => footerInputRef.current?.click()}
                    disabled={uploading.footer}
                    className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                  >
                    <Upload className="w-4 h-4" />
                    <span>{uploading.footer ? 'Uploading...' : 'Replace'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveImage('footer')}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span>Remove</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => footerInputRef.current?.click()}
                  disabled={uploading.footer}
                  className="flex items-center space-x-2 mx-auto px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  <span>{uploading.footer ? 'Uploading...' : 'Upload Footer Image'}</span>
                </button>
                <p className="mt-2 text-xs text-gray-500">PNG, JPG up to 5MB (Recommended: 1200x100px)</p>
              </div>
            </div>
          )}
          <input
            ref={footerInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload(file, 'footer');
            }}
          />
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Image Guidelines</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Logo: Square or rectangular, transparent background recommended</li>
            <li>Header: Wide format (1200x200px recommended) for top of reports/invoices</li>
            <li>Footer: Wide format (1200x100px recommended) for bottom of reports/invoices</li>
            <li>All images should be under 5MB in size</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
